import {
  ActionReducerMapBuilder,
  Dispatch,
  Draft,
  PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import {
  BebyggelseobjektBefintligPost,
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastIndex,
  HTTP_STATUS,
  ModelError,
  State
} from 'models';

import type { RootState } from './rootReducer';
import { errorOr, fulfilled, pending, rejected } from './storeHelpers';

import BebyggelseApi from 'api/bebyggelseApi';

const requestCondition = (getState: () => unknown): false | undefined => {
  const { bebyggelseobjektUtkast } = getState() as RootState;
  if (bebyggelseobjektUtkast.requestStatus === 'pending') return false;
};

export const getBebyggelseobjektUtkast = createAsyncThunk<
  Array<BebyggelseobjektUtkastIndex>,
  string,
  { rejectValue: ModelError }
>(
  'get/bebyggelseobjektutkast',
  async (id: string, { rejectWithValue }): Promise<any> => {
    const response: Response = await BebyggelseApi.aktivitet.getUtkast(id);
    return errorOr(response, rejectWithValue, [HTTP_STATUS.INTERNAL_SERVER_ERROR, HTTP_STATUS.SERVICE_UNAVAILABLE]);
  },
  {
    condition: (_id: string, { getState }) => requestCondition(getState)
  }
);

export const postInventeringUtkast = createAsyncThunk<
  BebyggelseobjektUtkastIndex[],
  { bebyggelseobjektBefintligPost: BebyggelseobjektBefintligPost; ignoreHttpStatus?: Array<HTTP_STATUS> },
  { rejectValue: ModelError }
>(
  'post/inventering/utkast',
  async (
    data: { bebyggelseobjektBefintligPost: BebyggelseobjektBefintligPost; ignoreHttpStatus?: Array<HTTP_STATUS> },
    { rejectWithValue }
  ) => {
    const response = await BebyggelseApi.aktivitet.postUtkast(data.bebyggelseobjektBefintligPost);
    return errorOr(response, rejectWithValue, data.ignoreHttpStatus);
  },
  {
    condition: (
      _utkastPost: {
        bebyggelseobjektBefintligPost: BebyggelseobjektBefintligPost;
        ignoreHttpStatus: Array<HTTP_STATUS>;
      },
      { getState }
    ) => requestCondition(getState)
  }
);

const initialState: State<BebyggelseobjektUtkastIndex[]> = {
  data: undefined,
  error: undefined,
  requestStatus: 'idle',
  requestType: undefined,
  currentRequestId: undefined
};

export const clearBebyggelseobjektUtkast = () => {
  return (dispatch: Dispatch) => {
    dispatch(clearBebyggelseobjektUtkastState());
  };
};

const BebyggelseobjektUtkastSlice = createSlice({
  name: 'bebyggelseobjektUtkast',
  initialState,
  reducers: {
    updateBebyggelseobjektUtkast: (
      state: Draft<State<BebyggelseobjektUtkastIndex[]>>,
      payloadAction: PayloadAction<string>
    ): void => {
      if (state.data) {
        state.data = state.data.filter(
          (utkast: BebyggelseobjektUtkastIndex) => utkast.utkast_id !== payloadAction.payload
        );
      }
    },
    addBebyggelseobjektUtkast: (
      state: Draft<State<BebyggelseobjektUtkastIndex[]>>,
      payloadAction: PayloadAction<BebyggelseobjektUtkast[]>
    ): void => {
      const utkastIndex: BebyggelseobjektUtkastIndex[] = payloadAction.payload.map((item) => ({
        geometri: item.geometri,
        titel: item.titel,
        utkast_id: item.utkast_id,
        bebyggelsenummer: item.bebyggelsenummer,
        bebyggelseobjektgrupp: item.bebyggelseobjektgrupp,
        berorda_fastigheter: item.berorda_fastigheter,
        lm_registerbyggnad: item.lm_registerbyggnad,
        lm_registerenhetsreferens: item.lm_registerenhetsreferens,
        registrering: item.registrering,
        skydd: item.skydd,
        ursprungligt_primar_andamal: item.ursprungligt_primar_andamal,
        havt_skydd: item.sammansatt?.havt_skydd || false
      }));
      if (state.data) {
        state.data.push(...utkastIndex);
      } else {
        state.data = utkastIndex;
      }
    },
    clearBebyggelseobjektUtkastState: (state: Draft<State<BebyggelseobjektUtkastIndex[]>>): void => {
      state.data = undefined;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<State<BebyggelseobjektUtkastIndex[]>>>): void => {
    builder.addCase(
      getBebyggelseobjektUtkast.pending,
      (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void => pending(state, action)
    );
    builder.addCase(
      getBebyggelseobjektUtkast.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void => fulfilled(state, action)
    );
    builder.addCase(
      getBebyggelseobjektUtkast.rejected,
      (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void => rejected(state, action)
    );

    //POST befintligt objekt till inventering
    builder.addCase(postInventeringUtkast.pending, (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void =>
      pending(state, action)
    );
    builder.addCase(
      postInventeringUtkast.fulfilled,
      (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void => {
        fulfilled(state, action);
      }
    );
    builder.addCase(
      postInventeringUtkast.rejected,
      (state: Draft<State<BebyggelseobjektUtkastIndex[]>>, action): void => rejected(state, action)
    );
  }
});

export const { updateBebyggelseobjektUtkast, clearBebyggelseobjektUtkastState, addBebyggelseobjektUtkast } =
  BebyggelseobjektUtkastSlice.actions;

export default BebyggelseobjektUtkastSlice.reducer;
